import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getAuthMe, getTelegramBotLink, getTelegramLoginStatus, postAuthLogin, postTelegramLoginLink, postTelegramMiniAppAuth } from '../../services/api';
import { User, Lock, Eye, EyeOff, Send } from 'lucide-react';
import { useAutoTranslate } from '../../hooks/useAutoTranslate';
import { useAuth } from '../../context/AuthContext';
import styles from './Login.module.css';
import { isTelegramMiniApp } from '../../utils/telegram';

const Login = () => {
  const TG_STATE_KEY = 'telegram_login_state';
  const TG_STATE_TTL = 10 * 60 * 1000; // 10 минут
  const [botLink, setBotLink] = useState((window.__BOT_LINK__ || '').trim());
  const [botLinkLoading, setBotLinkLoading] = useState(false);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [tgState, setTgState] = useState('');
  const [tgLink, setTgLink] = useState('');
  const [tgStatus, setTgStatus] = useState('');
  const [tgPolling, setTgPolling] = useState(false);
  const [miniAppInfo, setMiniAppInfo] = useState('');
  const [isMiniApp, setIsMiniApp] = useState(false);
  const pollRef = useRef(null);
  const miniAppAttempted = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshUser } = useAuth();
  const systemName = useAutoTranslate("CONNEXTA");

  const redirectTarget = (() => {
    const from = location.state?.from;
    const nextParam = new URLSearchParams(location.search).get('next');
    if (from?.pathname) {
      const search = from?.search || '';
      return `${from.pathname}${search}`;
    }
    if (nextParam && nextParam.startsWith('/')) {
      return nextParam;
    }
    return '/select-project';
  })();

  const clearPollTimer = () => {
    if (pollRef.current) {
      clearTimeout(pollRef.current);
      pollRef.current = null;
    }
  };

  const storeState = (state) => {
    try {
      localStorage.setItem(TG_STATE_KEY, JSON.stringify({ state, ts: Date.now() }));
    } catch {
      /* ignore */
    }
  };

  const clearStoredState = () => {
    try {
      localStorage.removeItem(TG_STATE_KEY);
    } catch {
      /* ignore */
    }
  };

  const restoreState = () => {
    try {
      const raw = localStorage.getItem(TG_STATE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed?.state || !parsed?.ts) return null;
      if (Date.now() - parsed.ts > TG_STATE_TTL) {
        clearStoredState();
        return null;
      }
      return parsed.state;
    } catch {
      return null;
    }
  };

  useEffect(() => () => clearPollTimer(), []);

  const fetchBotLink = useCallback(async () => {
    if (botLinkLoading) return botLink;
    setBotLinkLoading(true);
    try {
      const { data } = await getTelegramBotLink();
      const link = data?.bot_link?.trim();
      if (link) {
        setBotLink(link);
        return link;
      }
    } catch (err) {
      console.warn('Login: не удалось получить ссылку бота', err);
    } finally {
      setBotLinkLoading(false);
    }
    return '';
  }, [botLink, botLinkLoading]);

  useEffect(() => {
    if (!botLink) {
      fetchBotLink();
    }
  }, [botLink, fetchBotLink]);

  const completeLogin = useCallback(async (payload) => {
    const { access, refresh, user_full_name, user } = payload || {};
    const userFullName = user_full_name || user?.full_name || user?.username || username;
    if (!access || !refresh) {
      console.warn('Login: completeLogin called without tokens', payload);
      return;
    }
    clearStoredState();
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    localStorage.setItem('user_full_name', userFullName);
    try {
      await refreshUser();
    } catch (err) {
      console.warn('Login: refreshUser failed after telegram login, continuing', err);
    }
    setIsSuccess(true);
    setIsLoading(false);

    setTimeout(() => {
      const container = document.querySelector(`.${styles.loginContainer}`);
      const box = document.querySelector(`.${styles.loginBox}`);
      const logoSection = document.querySelector(`.${styles.logoSection}`);
      const logoIcon = document.querySelector(`.${styles.logoIcon}`);
      const form = document.querySelector(`.${styles.loginForm}`);
      
      if (container) container.classList.add(styles.exitAnimation);
      if (box) box.classList.add(styles.exitAnimation);
      if (logoSection) logoSection.classList.add(styles.exitAnimation);
      if (logoIcon) logoIcon.classList.add(styles.exitAnimation);
      if (form) form.classList.add(styles.exitAnimation);
      
      setTimeout(() => {
        navigate(redirectTarget, { replace: true });
      }, 800);
    }, 800);
  }, [navigate, redirectTarget, refreshUser, username]);

  useEffect(() => {
    const detectedMiniApp = isTelegramMiniApp();
    setIsMiniApp(detectedMiniApp);
    setMiniAppInfo('');
    const tryMiniAppAuth = async () => {
      if (miniAppAttempted.current) return;
      if (!detectedMiniApp || !window.Telegram?.WebApp?.initData) return;
      miniAppAttempted.current = true;
      setError('');
      // Не блокируем интерфейс: автологин пытается в фоне
      setTgStatus('');
      try {
        const { data } = await postTelegramMiniAppAuth({
          init_data: window.Telegram.WebApp.initData,
        });
        if (data?.access && data?.refresh) {
          setMiniAppInfo('');
          await completeLogin({
            access: data.access,
            refresh: data.refresh,
            user_full_name: data?.user?.full_name || data?.user?.username,
          });
          setTgStatus('');
          return;
        }
        setMiniAppInfo('');
      } catch (err) {
        console.warn('Miniapp auth skipped', err);
        setTgStatus('');
        setMiniAppInfo('');
        setIsLoading(false);
      }
    };
    if (detectedMiniApp) {
      setMiniAppInfo('');
    }
    tryMiniAppAuth();
  }, [completeLogin]);

  const pollTelegramStatus = useCallback(async (state, attempt = 0) => {
    if (!state) return;
    setTgPolling(true);
    setTgStatus('Ждём подтверждение в Telegram...');
    const MAX_ATTEMPTS = 40; // ~80 секунд при интервале 2с

    try {
      const { data } = await getTelegramLoginStatus({ state });
      if (data?.access && data?.refresh) {
        console.log('Login: получены токены из telegram login-status', data);
        setTgStatus('Авторизация через Telegram подтверждена, входим...');
        clearPollTimer();
        clearStoredState();
        await completeLogin(data);
        setTgPolling(false);
        return;
      }
    } catch (err) {
      console.warn('Telegram login poll error', err);
      setTgStatus('Ошибка при получении статуса авторизации, пробуем снова...');
    }

    if (attempt >= MAX_ATTEMPTS) {
      setTgStatus('Не удалось подтвердить вход через Telegram. Попробуйте ещё раз.');
      setTgPolling(false);
      clearPollTimer();
      return;
    }

    pollRef.current = setTimeout(() => pollTelegramStatus(state, attempt + 1), 2000);
  }, [completeLogin]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const incomingState = params.get('tg_state') || params.get('telegram_state');
    const restored = incomingState || restoreState();
    if (restored && restored !== tgState) {
      setTgState(restored);
      pollTelegramStatus(restored, 0);
    }
  }, [location.search, pollTelegramStatus, tgState]);

  const startTelegramLogin = async (options = {}, clickEvent = null) => {
    const telegramApp = window.Telegram?.WebApp;
    const isMiniAppContext = Boolean(telegramApp?.initData);
    // Не мешаем открыть новую вкладку по умолчанию, если это браузер
    if (isLoading || isSuccess || tgPolling) {
      if (isMiniAppContext && clickEvent) clickEvent.preventDefault();
      return;
    }
    setError('');
    clearPollTimer();
    const state = (crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`).replace(/-/g, '');
    setTgState(state);
    storeState(state);
    let linkSource = tgLink || botLink;
    if (!linkSource) {
      linkSource = await fetchBotLink();
    }
    if (!linkSource) {
      setTgStatus('Ссылка на бота не настроена. Обратитесь к администратору.');
      if (isMiniAppContext && clickEvent) clickEvent.preventDefault();
      return;
    }

    const fallbackLink = `${linkSource}?start=${encodeURIComponent(`login_${state}`)}`;
    const redirectUrl = `${window.location.origin}/login?tg_state=${state}`;
    let deepLink = fallbackLink;
    setTgLink(fallbackLink);
    if (!isMiniAppContext && clickEvent?.currentTarget) {
      clickEvent.currentTarget.setAttribute('href', fallbackLink);
    }
    setTgStatus('Открываем Telegram для подтверждения...');
    try {
      const { data } = await postTelegramLoginLink({
        state,
        redirect_url: redirectUrl,
        user_id: options.userId,
      });
      deepLink = data?.deep_link || fallbackLink;
      setTgLink(deepLink);
      if (!isMiniAppContext && clickEvent?.currentTarget) {
        clickEvent.currentTarget.setAttribute('href', deepLink);
      }
      console.log('Login: telegram login link', deepLink);
    } catch (err) {
      console.warn('Fallback to default telegram link', err);
    }
    if (isMiniAppContext) {
      if (clickEvent) clickEvent.preventDefault();
      try {
        telegramApp.openTelegramLink(deepLink);
      } catch (err) {
        console.warn('Telegram WebApp openTelegramLink failed, fallback to openLink', err);
        telegramApp.openLink?.(deepLink);
      }
    } else if (!clickEvent) {
      try {
        const w = window.open(deepLink, '_blank', 'noopener');
        if (!w) {
          console.warn('Login: popup blocked, ссылка доступна ниже');
        }
      } catch (err) {
        console.warn('Login: auto-open telegram link failed', err);
      }
    }
    pollTelegramStatus(state, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Получаем токены
      const tokenResponse = await postAuthLogin({ username, password });
      const { access, refresh, user_full_name, user } = tokenResponse.data || {};
      const resolvedUserFullName = user_full_name || user?.full_name || user?.username || username;

      // Сохраняем токены
      localStorage.setItem('access_token', access);
      localStorage.setItem('refresh_token', refresh);
      localStorage.setItem('user_full_name', resolvedUserFullName);
      console.log('Login: Токены сохранены');

      // Получаем актуальные данные пользователя
      const userResponse = await getAuthMe();
      const { telegram_chat_id, id: userId } = userResponse.data;

      // Проверяем telegram_chat_id
      if (!telegram_chat_id) {
        // Отправляем пользователя в Telegram для привязки chat_id
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_full_name');
      clearStoredState();
      clearPollTimer();
      setIsLoading(false);
      setTgStatus('Ваш аккаунт не привязан к Telegram. Откройте бота для подтверждения.');
      await startTelegramLogin({ userId });
      return;
      }

      console.log('Login: Данные пользователя обновлены, telegram_chat_id присутствует');

      // Обновляем данные в AuthContext
      await refreshUser();

      await completeLogin({ access, refresh, user_full_name: resolvedUserFullName, user });
      clearStoredState();

    } catch (err) {
      console.error('Login: Ошибка авторизации:', err);

      // Определяем тип ошибки и показываем соответствующее сообщение
      if (err.response) {
        // Сервер вернул ошибку
        if (err.response.status === 401) {
          setError('Неверное имя пользователя или пароль');
        } else if (err.response.status === 400) {
          setError('Пожалуйста, проверьте введённые данные');
        } else if (err.response.status >= 500) {
          setError('Ошибка сервера. Пожалуйста, попробуйте позже');
        } else {
          setError('Ошибка авторизации. Пожалуйста, попробуйте ещё раз');
        }
      } else if (err.request) {
        // Запрос был отправлен, но ответа не получено
        setError('Ошибка соединения. Проверьте подключение к интернету');
      } else {
        // Что-то пошло не так при настройке запроса
          setError('Произошла ошибка. Пожалуйста, попробуйте ещё раз');
      }

      // Очищаем токены при ошибке
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user_full_name');
      setIsLoading(false);
      setIsSuccess(false);
    }
  };

  return (
    <div className={styles.loginContainer}>
      <div className={styles.loginBox}>
        <div className={styles.logoSection}>
          <div className={styles.logoContainer}>
            <img src="/logo.png" alt="CONNEXTA SYSTEM" className={styles.logoImage} />
          </div>
        </div>
        {error && (
          <div className={styles.alert}>
            {error.includes('Telegram') ? (
              <>
                Ваш аккаунт не привязан к Telegram. Пожалуйста, авторизуйтесь через Telegram-бот{' '}
                { (tgLink || botLink) ? (
                  <a
                    href={tgLink || botLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.alertLink}
                  >
                    {(tgLink || botLink).replace('https://t.me/', '@')}
                  </a>
                ) : (
                  '— ссылка на бота не настроена'
                )}
              </>
            ) : (
              error
            )}
          </div>
        )}
        <form onSubmit={handleSubmit} className={styles.loginForm}>
          <div className={styles.formGroup}>
            <label htmlFor="username" className={styles.label}>
              Имя пользователя
            </label>
            <div className={styles.inputWrapper}>
              <User className={styles.inputIcon} />
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className={styles.input}
                placeholder="Введите имя пользователя"
                required
                disabled={isLoading || isSuccess}
              />
            </div>
          </div>
          <div className={styles.formGroup}>
            <label htmlFor="password" className={styles.label}>
              Пароль
            </label>
            <div className={styles.inputWrapper}>
              <Lock className={styles.inputIcon} />
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={styles.input}
                placeholder="Введите пароль"
                required
                disabled={isLoading || isSuccess}
              />
              <div
                className={styles.passwordToggle}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <EyeOff className={styles.toggleIcon} />
                ) : (
                  <Eye className={styles.toggleIcon} />
                )}
              </div>
            </div>
          </div>
          <button 
            type="submit" 
            className={`${styles.loginButton} ${isLoading ? styles.loading : ''} ${isSuccess ? styles.success : ''}`}
            disabled={isLoading || isSuccess}
          >
            {isSuccess ? (
              <div className={styles.successIcon}>
                <div className={styles.checkmarkCircle}>
                  <span className={styles.checkmark}>✓</span>
                </div>
              </div>
            ) : isLoading ? (
              <div className={styles.loadingSpinner}></div>
            ) : (
              'Войти'
            )}
          </button>
        </form>

        <div className={styles.telegramBlock}>
          <div className={styles.telegramHeader}>
            <div>
              <div className={styles.telegramTitle}>Войти через Telegram</div>
              <div className={styles.telegramSubtitle}>Доступно после первого входа по логину и паролю</div>
            </div>
          </div>
        <a
          href={tgLink || botLink || undefined}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.telegramButton}
          onClick={(e) => startTelegramLogin({}, e)}
          aria-disabled={tgPolling || isLoading || isSuccess}
        >
          <Send size={18} />
          <span>{tgPolling ? 'Ожидаем подтверждение...' : 'Открыть бота'}</span>
        </a>
        {tgLink && (
          <a href={tgLink} target="_blank" rel="noopener noreferrer" className={styles.telegramLink}>
            Если ссылка не открылась, нажмите здесь
          </a>
        )}
          {tgStatus && <div className={styles.telegramStatus}>{tgStatus}</div>}
        </div>
      </div>
    </div>
  );
};

export default Login;
